﻿import {Delegate} from "../Tools/Delegate.js";
import {String} from "../Tools/Misc.js"
import {tokenLocalStorageKey} from "./TokenHelper.js"
import {NotificationHelper, NotificationToolbar} from "./NotificationHelper.js";
import {DateTime} from "../Tools/Misc.js";
import {ResponseApi} from "../Model/ResponseApi.js";
import {HandledError} from "../Tools/HandledError.js";
import {OnFetchVersionChanged} from "../Delegates/FetchDelegate";

export const OnTokenRefreshed: Delegate = new Delegate();


export abstract class FetchAbstract {

    static #VERSION_HEADER = "BO-App-Version";

    static #_version: string | undefined;
    public static get Version() {
        return this.#_version;
    }

    public static set Version(value: string) {
        if (this.#_version != value) {
            OnFetchVersionChanged.Fire(value);
        }

        this.#_version = value;
    }

    public static Get = async <T>(url: string, headers: Headers = null): Promise<T> => {

        const intInfo: RequestInit = {
            method: "GET",
            headers: headers,
        }

        if (headers != null) intInfo.headers = headers;
        return this.runFetch<T>(url, intInfo);
    }

    public static Post = async <T>(url: string, body: any = null, headers: Headers = null): Promise<T> => {
        headers = headers == null ? new Headers() : headers;

        const intInfo: RequestInit = {
            method: "POST",
            headers: headers,
        }

        if (body != null) intInfo.body = JSON.stringify(body);
        return this.runFetch<T>(url, intInfo);
    }

    public static Put = async <T>(url: string, body: any = null, headers: Headers = null): Promise<T> => {
        headers = headers == null ? new Headers() : headers;

        const intInfo: RequestInit = {
            method: "PUT",
            headers: headers,
        }

        if (body != null) intInfo.body = JSON.stringify(body);
        return this.runFetch<T>(url, intInfo);
    }

    public static Delete = async <T>(url: string, body: any = null, headers: Headers = null): Promise<T> => {
        headers = headers == null ? new Headers() : headers;

        const intInfo: RequestInit = {
            method: "DELETE",
            headers: headers,
        }

        if (body != null) intInfo.body = JSON.stringify(body);
        return this.runFetch<T>(url, intInfo);
    }

    // URL du endpoint de rotation silencieuse du cookie B-Account. Appelé
    // une seule fois lorsqu'une requête revient en 401 (cookie d'accès
    // expiré) : il régénère le cookie via B-RefreshToken côté serveur.
    static #REFRESH_URL = "/auth/refresh";

    // Verrou : si plusieurs requêtes tombent en 401 simultanément, un seul
    // appel à /auth/refresh est émis ; les autres attendent la même promesse.
    static #refreshing: Promise<boolean> | null = null;

    /**
     * Tente une rotation du cookie B-Account via /auth/refresh. Mutualise
     * l'appel concurrent grâce à #refreshing. Renvoie true si le serveur a
     * régénéré le cookie (HTTP 200), false sinon.
     */
    static #tryRefreshCookie = async (): Promise<boolean> => {
        if (this.#refreshing != null)
            return this.#refreshing;

        this.#refreshing = fetch(this.#REFRESH_URL, {method: "POST"})
            .then(r => r.ok)
            .catch(() => false)
            .finally(() => {
                this.#refreshing = null;
            });

        return this.#refreshing;
    }

    protected static runFetch = async <T>(url: string, init: RequestInit, isRetry: boolean = false): Promise<T> => {

        NotificationToolbar.Instance.FetchMessage(`Fetching ... ${init.method.toUpperCase()} ${url}`).then();
        const t = window.localStorage.getItem(tokenLocalStorageKey);
        if (!String.IsNullOrEmpty(t)) {
            const h = init.headers as Headers;
            h.append("Authorization", `bearer ${t}`);
        }

        return fetch(url, init)
            .then(async (response: Response) => {

                if (response.headers.has(this.#VERSION_HEADER)) {
                    this.Version = response.headers.get(this.#VERSION_HEADER);
                }

                if (response.headers.has("R-Token"))
                    OnTokenRefreshed.Fire(response.headers.get("R-Token"));

                if (response.status == 401) {
                    // Cookie d'accès expiré. On tente UNE fois une rotation
                    // silencieuse via /auth/refresh, puis on rejoue la requête
                    // d'origine (transparent pour l'utilisateur, sans reload).
                    // L'appel à /auth/refresh lui-même n'est jamais réintercepté.
                    if (!isRetry && url !== this.#REFRESH_URL && await this.#tryRefreshCookie()) {
                        return this.runFetch<T>(url, init, true);
                    }

                    NotificationToolbar.Instance.FetchMessage(`Fetching ... ${init.method.toUpperCase()} ${url} KO`).then();
                    location.href = "/";
                    return {statut: response.status}
                }

                if (response.ok === true) {
                    const contentType = response.headers.get("content-type");
                    if (contentType && contentType.includes("application/json")) {
                        const result = await response.json();
                        FetchAbstract.checkResponseApi(result);
                        NotificationToolbar.Instance.FetchMessage(`Fetching ... ${init.method.toUpperCase()} ${url} => OK`).then();
                        return DateTime.ConvertDates(result);
                    } else {
                        const result = await response.text();
                        NotificationToolbar.Instance.FetchMessage(`Fetching ... ${init.method.toUpperCase()} ${url} => KO`).then();
                        return result as any;
                    }
                }


                NotificationHelper.Instance.error("Une erreur s'est produite.").then();
                return {statut: response.status}

            }).catch((error: any) => {
                console.error(error);
            });
    }

    private static checkResponseApi = (obj: any) => {
        if (!FetchAbstract.IsResponseApi(obj))
            return;

        const r = obj as ResponseApi<any>;
        if (r.Status == 500) {
            if (!r.HasError) {
                throw new HandledError("Une erreur non spécifiée s'est produite")
            }

            const err = r.Errors[0];
            throw new HandledError(err.Message);
        }

        if (obj.Status != 200)
            throw new HandledError(
                `Erreur lors de l'appel de l'API. Status : ${obj.status}`);

        if (obj.HasError) {
            obj.Errors.forEach(x => {
                NotificationHelper.Instance.error(x.Message)
            });
        }
    }

    private static IsResponseApi = (obj: any) => {
        return obj &&
            'Data' in obj &&
            'Status' in obj &&
            'HasError' in obj &&
            Array.isArray(obj.Errors);

    }
}